import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase'; // Adjust path as needed
import { onAuthStateChanged } from 'firebase/auth';
import { dataService } from '../services/dataService'; // Adjust path
import { auditService } from '../services/auditService'; // Added import for auditService
// Helper Hook
export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authLoadingStatus, setAuthLoadingStatus] = useState('Initialiseren...');
    const [adminMode, setAdminMode] = useState(false);
    const [authError, setAuthError] = useState(null);

    useEffect(() => {
        let profileUnsubscribe = null;

        const authUnsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setLoading(true);
            setAuthLoadingStatus('Authenticeren...');

            if (profileUnsubscribe) {
                profileUnsubscribe();
                profileUnsubscribe = null;
            }

            if (firebaseUser) {
                try {
                    // Subscribe to Profile
                    profileUnsubscribe = dataService.subscribeToUserProfile(firebaseUser.uid, async (userProfile) => {
                        if (userProfile) {
                            // Permission logic based on roles/rules
                            // Canonical role codes (roleNames): 'user' = Werkvoorbereider, 'wv' = Werkverantwoordelijke.
                            const canCreatePlans = ['admin', 'superuser', 'user', 'wv'].includes(userProfile.role);
                            const fullUser = { ...userProfile, uid: firebaseUser.uid, canCreatePlans };
                            setUser(fullUser);
                            setAdminMode(false);
                            setAuthError(null);

                            // AUDIT: Log successful login/session start
                            // Bij meerdere locaties is de eerste een keuze, maar zichtbaar voor die
                    // beheerder is beter dan alleen voor de superuser.
                    auditService.logAction(fullUser.uid, 'AUTH_SESSION_START', { method: 'auto/refresh', canCreatePlans }, fullUser.allowedLocations?.[0] || null);

                        } else {
                            // GEEN PROFIEL. Hier werd eerst stil een monteursprofiel
                            // aangemaakt. Dat kan niet meer sinds zelfregistratie:
                            // een registrant met een monteursprofiel kan daarna geen
                            // bedrijf meer starten, want je eigen rol wijzigen mag
                            // niet. De keuze (nieuw bedrijf of bestaand team) ligt
                            // nu bij de gebruiker, in het Onboarding-scherm.
                            console.warn("[AuthContext] Geen profiel voor UID:", firebaseUser.uid);
                            setUser({
                                uid: firebaseUser.uid,
                                email: firebaseUser.email,
                                emailVerified: firebaseUser.emailVerified,
                                geenProfiel: true
                            });
                            setLoading(false);
                        }
                        if (userProfile) {
                            setLoading(false);
                        }
                    });
                } catch (error) {
                    console.error("AuthContext Error:", error);
                    setAuthError(error.message);
                    setLoading(false);
                    // AUDIT: Log error during profile subscription
                    // Bewust zonder locatie: het profiel laden mislukte, dus de
                    // locaties zijn onbekend. Alleen de superuser ziet dit, en dat
                    // klopt, want dit is een platformfout en geen klantgebeurtenis.
                    auditService.logAction(firebaseUser.uid, 'AUTH_PROFILE_ERROR', { error: error.message });
                }
            } else {
                // Logged out
                if (user) { // Only log logout if there was a user previously
                    auditService.logAction(user.uid, 'AUTH_LOGOUT', {}, user.allowedLocations?.[0] || null);
                }
                setUser(null);
                setAdminMode(false);
                setLoading(false);
            }
        });

        return () => {
            if (authUnsubscribe) authUnsubscribe();
            if (profileUnsubscribe) profileUnsubscribe();
        };
    }, []);

    const value = {
        user,
        adminMode,
        setAdminMode, // Allow manual toggle if needed (though App.jsx had logic to auto-set it)
        loading,
        authLoadingStatus,
        authError
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
